import React, { useState } from 'react';
import { Box, FormControl, MenuItem, Select, Button, Typography, Paper, useMediaQuery, useTheme } from '@mui/material';
import { downloadFilesAsync } from '../../services/FileDownloader';
import { userHasAccess } from '../../services/RoleBasedAccess';
import { useAssetsContext } from '../../context/AssetsContext';
import { useSession } from '../../context/SessionContext';
import { useAssetActions } from '../../hooks/asset/useAssetActions';

const formatOperationLabel = (operation: string) => {
  switch (operation) {
    case 'archive':
      return 'archive';
    case 'unarchive':
      return 'unarchive';
    case 'syndication':
      return 'send to syndication';
    case 'thumbnails':
      return 'regenerate thumbnails for';
    case 'exterior_bgr':
      return 'reprocess background for';
    case 'cropping':
      return 'reprocess cropped images for';
    case 'featured':
      return 'reprocess featured images for';
    default:
      return `trigger ${operation} for`;
  }
};

const buildBatchResultMessage = (
  operation: string,
  counts: { started: number; locked: number; failed: number; duplicate: number }
) => {
  const action = formatOperationLabel(operation);
  const parts: string[] = [];

  if (counts.started > 0) {
    parts.push(`${counts.started} ${counts.started === 1 ? 'asset was' : 'assets were'} queued to ${action}.`);
  }
  if (counts.locked > 0) {
    parts.push(`${counts.locked} ${counts.locked === 1 ? 'asset is' : 'assets are'} currently locked.`);
  }
  if (counts.failed > 0) {
    parts.push(`${counts.failed} ${counts.failed === 1 ? 'asset failed' : 'assets failed'} to start.`);
  }
  if (counts.duplicate > 0) {
    parts.push(`${counts.duplicate} duplicate ${counts.duplicate === 1 ? 'request was' : 'requests were'} skipped.`);
  }

  return parts.join(' ');
};

const AssetOperations: React.FC = () => {
  const { selectedAssets, setSelectedAssets, refetch } = useAssetsContext();
  const { user } = useSession();
  const { reprocessAsset, secondaryProcessAsset } = useAssetActions();
  const [selectedOperation, setSelectedOperation] = useState('');
  const role = user?.role || '';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onAssetsUpdate = () => {
    setSelectedAssets([]);
    refetch();
  };

  const executeOperation = async () => {
    if (!selectedOperation) return;

    try {
      if (selectedOperation === 'downloadImages') {
        selectedAssets.forEach((asset) => downloadFilesAsync(asset));
      } else if (selectedOperation === 'secondaryProcess') {
        selectedAssets.forEach((asset) => {
          secondaryProcessAsset(asset.pk, true);
        });
        alert(`Triggered secondary processing for ${selectedAssets.length} assets.`);
      } else {
        const results = await Promise.all(
          selectedAssets.map((asset) =>
            reprocessAsset(asset.pk, asset.sk, selectedOperation, { notify: false })
          )
        );

        const counts = results.reduce(
          (acc, result) => {
            acc[result.status] += 1;
            return acc;
          },
          { started: 0, locked: 0, failed: 0, duplicate: 0 }
        );

        if (selectedOperation === 'archive' || selectedOperation === 'unarchive') {
          onAssetsUpdate();
        }

        const message = buildBatchResultMessage(selectedOperation, counts);
        if (message) {
          alert(message);
        } else {
          alert(`No ${selectedOperation} requests were sent.`);
        }
      }
    } finally {
      setSelectedAssets([]);
      setSelectedOperation('');
    }
  };

  if (selectedAssets.length === 0) return null;

  return (
    <Paper sx={{ padding: 2, borderRadius: 2, marginBottom: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Asset Operations
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setSelectedAssets([])}
          sx={{
            textTransform: 'none',
            color: theme.palette.text.secondary,
            borderColor: theme.palette.divider,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.secondary,
            }
          }}
        >
          Clear ({selectedAssets.length})
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1.5 : 0 }}>
        <FormControl sx={{ width: isMobile ? '100%' : '50%', mr: isMobile ? 0 : 2 }}>
          <Select
            value={selectedOperation}
            onChange={(e) => setSelectedOperation(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="" disabled>Select an Operation...</MenuItem>
            <MenuItem value="downloadImages">Download Images</MenuItem>
            {userHasAccess(role, ['Internal']) && [
              <MenuItem value="cropping" key="cropping">Reprocess Cropped</MenuItem>,
              <MenuItem value="featured" key="featured">Reprocess Featured</MenuItem>,
              <MenuItem value="exterior_bgr" key="exterior_bgr">Reprocess Background</MenuItem>,
              <MenuItem value="thumbnails" key="thumbnails">Regenerate Thumbnails</MenuItem>,
              <MenuItem value="secondaryProcess" key="secondaryProcess">Secondary Process</MenuItem>
            ]}
            {userHasAccess(role, ['Internal', 'Client Plus']) && (
              <MenuItem value="syndication">Send to Syndication</MenuItem>
            )}
            {userHasAccess(role, ['Internal']) && (
              <MenuItem value="archive" key="archive">Archive</MenuItem>
            )}
            {selectedAssets.some((asset) => !!asset.archived) && userHasAccess(role, ['Internal']) && (
              <MenuItem value="unarchive">Unarchive</MenuItem>
            )}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="warning"
          onClick={executeOperation}
          disabled={!selectedOperation}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          Execute
        </Button>
      </Box>
    </Paper>
  );
};

export default AssetOperations;
